import type { ElementType, FormElement } from '../model/form.types';
import type { ConditionalRuleAction } from '../model/form-rules.store';
import { getElementRegistryEntry } from './element-registry';
import { iterateFormElements } from './form-element-traversal';
import { getRuleTriggerOptions } from './rule-trigger-options';

export const RULE_ACTION_LABELS: Record<ConditionalRuleAction, string> = {
  show: 'Mostrar',
  hide: 'Ocultar',
  disable: 'Desabilitar',
};

export const REPORT_FIELD_EMPTY_RULE = '-';

export interface ReportFieldExportRuleBlock {
  readonly action: ConditionalRuleAction | '';
  readonly targets: readonly { readonly id: string }[];
}

export interface ReportFieldExportRule {
  readonly triggerFieldId: string;
  readonly triggerOption: string;
  readonly thenBlocks?: readonly ReportFieldExportRuleBlock[];
}

export type ReportFieldExportAnswer = 'Sim' | 'Não';

export interface ReportFieldExportRow {
  identifier: string;
  field: string;
  hasRule: ReportFieldExportAnswer;
  rule: string;
  anchor: ReportFieldExportAnswer;
}

function toAnswer(value: boolean): ReportFieldExportAnswer {
  return value ? 'Sim' : 'Não';
}

function resolveFieldLabel(type: ElementType): string {
  return getElementRegistryEntry(type)?.label ?? type;
}

function buildElementIndex(elements: readonly FormElement[]): Map<string, FormElement> {
  const index = new Map<string, FormElement>();

  for (const element of iterateFormElements(elements, { includeTableContainers: true })) {
    index.set(element.id, element);
  }

  return index;
}

function resolveIdentifier(element: FormElement): string {
  return element.properties.name || element.id;
}

function sortRulesByTriggerOption(
  rules: ReportFieldExportRule[],
  triggerElement: FormElement
): ReportFieldExportRule[] {
  const optionOrder = new Map(
    getRuleTriggerOptions(triggerElement).map((option, position) => [option.value, position])
  );

  return rules
    .map((rule, position) => ({ rule, position }))
    .sort((first, second) => {
      const firstOrder = optionOrder.get(first.rule.triggerOption) ?? Number.MAX_SAFE_INTEGER;
      const secondOrder = optionOrder.get(second.rule.triggerOption) ?? Number.MAX_SAFE_INTEGER;

      if (firstOrder !== secondOrder) return firstOrder - secondOrder;
      return first.position - second.position;
    })
    .map((entry) => entry.rule);
}

function buildRuleCell(rule: ReportFieldExportRule, index: Map<string, FormElement>): string {
  const lines: string[] = [];

  (rule.thenBlocks ?? []).forEach((block) => {
    if (!block.action) return;

    const actionLabel = RULE_ACTION_LABELS[block.action];

    block.targets.forEach((target) => {
      const targetElement = index.get(target.id);
      const targetIdentifier = targetElement ? resolveIdentifier(targetElement) : target.id;
      lines.push(`${rule.triggerOption} -> ${actionLabel} -> ${targetIdentifier}`);
    });
  });

  return lines.length > 0 ? lines.join('\n') : REPORT_FIELD_EMPTY_RULE;
}

export function buildReportFieldsExportRows(
  elements: readonly FormElement[],
  rules: readonly ReportFieldExportRule[],
  reportFieldIds: readonly string[],
  anchorFieldIds: readonly string[]
): ReportFieldExportRow[] {
  const index = buildElementIndex(elements);
  const reportIds = new Set(reportFieldIds);
  const anchorIds = new Set(anchorFieldIds);
  const rows: ReportFieldExportRow[] = [];

  for (const element of iterateFormElements(elements, { includeTableContainers: true })) {
    if (!reportIds.has(element.id)) continue;

    const identifier = resolveIdentifier(element);
    const field = resolveFieldLabel(element.type);
    const anchor = toAnswer(anchorIds.has(element.id));
    const fieldRules = rules.filter((rule) => rule.triggerFieldId === element.id);

    if (fieldRules.length === 0) {
      rows.push({ identifier, field, hasRule: 'Não', rule: REPORT_FIELD_EMPTY_RULE, anchor });
      continue;
    }

    sortRulesByTriggerOption(fieldRules, element).forEach((rule) => {
      rows.push({ identifier, field, hasRule: 'Sim', rule: buildRuleCell(rule, index), anchor });
    });
  }

  return rows;
}
